import React, { Component } from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import moment from 'moment';
import classnames from 'classnames';

import {
  StandardBody,
  SmallestBody,
} from 'web-ui-components/lib/typography/body';
import { SubHeading } from 'web-ui-components/lib/typography/headings';
import { MostProminentValue } from 'web-ui-components/lib/typography/values';
import { LogoDisplay } from 'web-ui-components/lib/transactions/logo';
import { Icon } from 'web-ui-components/lib/atoms/iconography';
import { LoadingLocalTakeover } from 'web-ui-components/lib/organisms/takeovers';

import AnalyticsActionCreator from '../../actions/AnalyticsActionCreator';
import AnalyticsUtil from '../../utils/AnalyticsUtil';
import StepHeader from '../../includes/StepHeader';
import config from '../../config';
import envConfig from '../../../static/config';

export class EnrichedTransactionDetails extends Component {
  constructor(props) {
    super(props);

    window.addEventListener('message', this.onMapMessage);

    this.state = {
      isMapLoading: true,
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.onMapMessage);
  }

  onMapMessage = (event) => {
    if (event.origin !== envConfig.mapWebappOrigin) {
      return;
    }

    const { data: message } = event;
    const { type } = message;

    if (type === 'onSDKReady') {
      AnalyticsUtil.analyticAction(
        config.analytics.analytics_experience_activity,
        config.analytics.analytics_event_method_interacted,
        config.analytics.analytics_name_enriched_transaction_details,
        'WoosMap/WebConsumption'
      );

      AnalyticsActionCreator.requestImmediateDispatch();
    } else if (type === 'onMapReady') {
      this.setState({
        isMapLoading: false,
      });
    }
  };

  render() {
    const { content, data, handleBackButton } = this.props;
    const longDate = moment(data.dateRaw).format('dddd, Do MMMM, HH:mm');

    const logoInitials = data.isDescriptionEnriched
      ? data.description.charAt(0).toUpperCase()
      : data.merchantName.charAt(0).toUpperCase();

    const { geolocation, fullAddress, isCardHolderPresent } = data;

    return (
      <div className="page bow enriched-transaction-details">
        <Helmet title="Transaction Details" />

        <div id="site-main" className="container" role="main" tabIndex="-1">
          <div className="row">
            <div className="col-md1-18">
              {
                <StepHeader
                  showheaderCrumbs
                  headerCrumbsMessage={content.back}
                  onClick={handleBackButton}
                />
              }
            </div>
          </div>
          <div className="enriched-transaction-details__info-wrapper">
            <div className="enriched-transaction-details__info-wrapper__first-section">
              <LogoDisplay
                data-test-id="logo-display"
                imageSource={data.logo?.url}
                initials={logoInitials}
                alt={data.logo?.altTextTag}
                isSquare={!!data.logo?.url}
              />
              <div>
                <div className="enriched-transaction-details__info-wrapper__first-section__pending">
                  <SubHeading
                    data-test-id="description"
                    icon={
                      data.posted === false && (
                        <Icon iconType="PendingFilledLarge" size="md" />
                      )
                    }
                  >
                    {data.description}
                  </SubHeading>
                </div>
                <StandardBody data-test-id="date" isFlush>
                  <span>{longDate}</span>
                </StandardBody>
              </div>
            </div>
            <div className="enriched-transaction-details__info-wrapper__second-section">
              <MostProminentValue data-test-id="transaction-amount" isFlush>
                {data.amount}
              </MostProminentValue>
              <StandardBody data-test-id="transaction-type" isFlush>
                <span>{data.type}</span>
              </StandardBody>
              <SmallestBody
                data-test-id="transaction-statement-company"
                isFlush
              >
                <span>Shown on statement as {data.merchantName}</span>
              </SmallestBody>
              {data.reference && (
                <SmallestBody data-test-id="transaction-reference" isFlush>
                  <span>Reference: {data.reference}</span>
                </SmallestBody>
              )}
            </div>
          </div>
          <div className="enriched-transaction-details__balance">
            <StandardBody data-test-id="transaction-balance" isFlush>
              <span>Balance after transaction: {data.balance}</span>
            </StandardBody>
          </div>
          {isCardHolderPresent && geolocation && (
            <div
              className="enriched-transaction-details__map-wrapper"
              data-test-id="map-wrapper"
            >
              <LoadingLocalTakeover
                show={this.state.isMapLoading}
                title="Loading"
              />
              <iframe
                id="transaction-location-map"
                title="transaction-location-map"
                width="100%"
                height="475px"
                frameBorder="0"
                ref="mapFrame"
                src={`${envConfig.mapWebappOrigin}/?center=${geolocation.latitude},${geolocation.longitude}`}
                className={classnames(
                  'enriched-transaction-details__map-wrapper__iframe',
                  {
                    'enriched-transaction-details__map-wrapper__iframe--hidden':
                      this.state.isMapLoading,
                    'enriched-transaction-details__map-wrapper__iframe--address-available':
                      fullAddress,
                    'enriched-transaction-details__map-wrapper__iframe--address-unavailable':
                      !fullAddress,
                  }
                )}
              />
              {!this.state.isMapLoading && fullAddress && (
                <div
                  className="enriched-transaction-details__map-wrapper__address"
                  data-test-id="transaction-full-address"
                >
                  <StandardBody isFlush>{fullAddress}</StandardBody>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

EnrichedTransactionDetails.propTypes = {
  content: PropTypes.object.isRequired,
  handleBackButton: PropTypes.func.isRequired,
};
